import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext.jsx';
import { safeStorage } from './utils/safeStorage';
import Login from './Login.jsx';
import { isPlatformAdminUser } from './utils/platformAccess';
import { restrictedHome } from './utils/accountKind';

export default function RoleRedirect() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [hasAttemptedNavigation, setHasAttemptedNavigation] = useState(false);

  useEffect(() => {
    // Don't do anything while loading
    if (loading) {
      setHasAttemptedNavigation(false);
      return;
    }

    // If no user, reset navigation state
    if (!user) {
      setHasAttemptedNavigation(false);
      return;
    }

    // Don't navigate if we already tried
    if (hasAttemptedNavigation) return;

    // Mark that we're attempting navigation
    setHasAttemptedNavigation(true);

    // Navigate based on user role
    const navigateToRole = () => {
      if (restrictedHome(user)) {
        navigate(restrictedHome(user), { replace: true });
      } else if (user.role === 'super_admin') {
        if (isPlatformAdminUser(user)) {
          navigate('/platform', { replace: true });
        } else {
          // Organization super_admin goes to their organization's admin dashboard
          navigate('/admin', { replace: true });
        }
      } else if (user.role === 'admin') {
        navigate('/admin', { replace: true });
      } else if (user.role === 'custom') {
        navigate('/admin', { replace: true });
      } else if (user.role === 'provider') {
        // All providers (MHP, PCA, RN, etc.) go to admin dashboard with permissions based on providerType
        navigate('/admin', { replace: true });
      } else {
        // All other roles (Coordinator, Billing_Specialist, etc.) go to admin dashboard
        // Permissions are handled by the backend and sidebar filtering
        navigate('/admin', { replace: true });
      }
    };

    // Use setTimeout to prevent immediate navigation during render
    const timeoutId = setTimeout(navigateToRole, 0);
    return () => clearTimeout(timeoutId);

  }, [user, loading, navigate, hasAttemptedNavigation]);

  // Safety timeout: if still showing "Redirecting..." after 3s, navigation failed
  useEffect(() => {
    if (user && !loading) {
      const timeout = setTimeout(() => {
        // If we're still on this screen after 3s, navigation failed — clear and show login
        console.warn('Navigation timeout — clearing auth state');
        safeStorage.removeItem('evv_user_cache');
        safeStorage.removeItem('evv_token');
        window.location.href = '/';
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [user, loading]);

  // Show loading spinner while authentication is being determined
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Show login if no user
  if (!user) {
    return <Login />;
  }
  
  // Show brief loading while navigation happens
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600 dark:text-gray-400">Redirecting...</p>
      </div>
    </div>
  );
}
